/**
 * scriptParser — A/V script parser/serializer for the Video Generator's
 * `in_motion_prompt` field.
 *
 * Layer on top of the existing motion prompt parser.
 * Adds:
 *   - Per-block client IDs (so the editor can address each block stably).
 *   - Track separation: `videoBlocks` (action) vs `audioBlocks`
 *     (dialogue / voiceover / sfx).
 *   - Disable reason detection for Structured mode (BGM lines, text with no
 *     timestamped segments at all).
 *   - Preserved clip-wide preamble before the first timestamp, in whatever form
 *     the author wrote it — `[CINEMATIC COLOR GRADE: ...]`, `SEATING LOCK: ...`,
 *     `OFF-CENTER COMPOSITION: ...`, `좌석 고정: ...`.
 *   - Preserved trailing content after the last segment, e.g. a `<voices>`
 *     roster.
 *   - The `PREVIOUS SHOT CONTEXT, follow naturally:` continuity paragraph,
 *     rendered as a clip-wide note and serialized right after the preamble.
 *   - Video & audio overlap detection.
 *   - Byte-stable serialization back to `in_motion_prompt`.
 */

import {
  parseMotionPrompt,
  type MotionSegment,
} from './motionPromptParser'
import { EMPTY_SPEAKER_NAME_INDEX, type SpeakerNameIndex } from './speakerToken'

export type SegmentKind = 'video' | 'dialogue' | 'voiceover' | 'sfx' | 'bgm'

export interface ScriptBlock {
  id: string
  kind: SegmentKind
  startSec: number
  endSec: number
  text: string
  character?: string
  emotion?: string
  camera?: string
}

export type StructuredDisable =
  | { reason: 'bgm-present'; sample: string }
  | { reason: 'unparseable' }

export interface ParsedScript {
  clipHeader: string | null
  globalPreamble: string | null
  /** Content after the last timestamped segment, preserved verbatim. */
  trailingText: string | null
  /** The `PREVIOUS SHOT CONTEXT, follow naturally:` paragraph. It describes the
   * previous clip's ending state and applies to the whole clip, so it is not
   * part of any segment's time range. */
  previousShotContext: string | null
  videoBlocks: ScriptBlock[]
  audioBlocks: ScriptBlock[]
  videoOverlap: Array<[string, string]>
  audioOverlapCount: number
  canStructured: boolean
  disableReason: StructuredDisable | null
}

/* ── ID generation ── */

let _counter = 0
function generateId(): string {
  // Use crypto.randomUUID when available; fall back to counter for SSR / tests.
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {
    // ignore
  }
  _counter += 1
  return `blk-${_counter}`
}

/* ── Helpers ── */

const SFX_PREFIX_RE = /^\s*(SFX|Sound)\s*:/i
const BGM_LINE_RE = /^\s*BGM\s*[:-]/im
const TIMESTAMP_RE = /\[\d+(?:\.\d+)?s?-\d+(?:\.\d+)?s\]/
// Emitted by the motion-prompt-writing skill after the last timestamped action
// segment and before any tagged metadata.
const PREVIOUS_SHOT_CONTEXT_RE = /^[ \t]*PREVIOUS SHOT CONTEXT, follow naturally:/m
// A segment starts on its own line; an inline `[6.0s-8.0s]` reference inside the
// continuity paragraph is prose, not a boundary.
const LINE_START_TIMESTAMP_RE = /^[ \t]*\[\d+(?:\.\d+)?s?-\d+(?:\.\d+)?s\]/m

function trimSample(text: string, limit = 60): string {
  const t = text.trim().replace(/\s+/g, ' ')
  return t.length > limit ? `${t.slice(0, limit)}…` : t
}

function segmentToBlock(seg: MotionSegment): ScriptBlock {
  if (seg.type === 'action') {
    return {
      id: generateId(),
      kind: 'video',
      startSec: seg.startTime,
      endSec: seg.endTime,
      text: seg.action,
      camera: seg.camera ?? undefined,
    }
  }
  // dialogue or voiceover — check for SFX shorthand.
  // Existing parser may have promoted "SFX:" into the `character` slot via
  // the colon-fallback path; treat both cases as SFX.
  const characterLooksLikeSfx = /^(SFX|Sound)$/i.test((seg.character ?? '').trim())
  const bodyStartsWithSfx = SFX_PREFIX_RE.test(seg.action)
  const isSfx = characterLooksLikeSfx || bodyStartsWithSfx
  let text = seg.action
  if (bodyStartsWithSfx) {
    text = seg.action.replace(SFX_PREFIX_RE, '').trim()
  }
  return {
    id: generateId(),
    kind: isSfx ? 'sfx' : seg.type === 'voiceover' ? 'voiceover' : 'dialogue',
    startSec: seg.startTime,
    endSec: seg.endTime,
    text,
    character: isSfx ? undefined : seg.character,
    emotion: isSfx ? undefined : seg.emotion,
  }
}

function overlaps(a: ScriptBlock, b: ScriptBlock): boolean {
  return a.startSec < b.endSec && b.startSec < a.endSec
}

function detectOverlapPairs(blocks: ScriptBlock[]): Array<[string, string]> {
  const pairs: Array<[string, string]> = []
  for (let i = 0; i < blocks.length; i++) {
    for (let j = i + 1; j < blocks.length; j++) {
      if (overlaps(blocks[i], blocks[j])) {
        pairs.push([blocks[i].id, blocks[j].id])
      }
    }
  }
  return pairs
}

function maxConcurrent(blocks: ScriptBlock[]): number {
  if (blocks.length === 0) return 0
  // Sweep over time-points.
  const events: Array<{ t: number; delta: number }> = []
  for (const b of blocks) {
    events.push({ t: b.startSec, delta: 1 })
    events.push({ t: b.endSec, delta: -1 })
  }
  // End-events must be processed before start-events at the same t,
  // so two touching blocks aren't counted as overlapping.
  events.sort((a, b) => (a.t - b.t) || (a.delta - b.delta))
  let cur = 0
  let max = 0
  for (const ev of events) {
    cur += ev.delta
    if (cur > max) max = cur
  }
  return max
}

function formatSec(n: number): string {
  // Mirror existing serialization style: `1.0s-3.5s` (one decimal).
  return `${(Math.round(n * 10) / 10).toFixed(1)}s`
}

/** Split the text before the first timestamp into an optional clip header and
 * a clip-wide preamble.
 *
 * Every remaining line is kept verbatim as `globalPreamble` — there is no
 * allowlist of recognized directive shapes. Authors write clip-wide constraints
 * in whatever form they like (`[SEATING LOCK: ...]`, `CINEMATIC LOCK: ...`,
 * `OFF-CENTER COMPOSITION: ...`, `좌석 고정: ...`), and Structured mode stays
 * available because the timestamped body is what it actually edits. The
 * preamble is rendered read-only above the timeline and round-tripped by
 * `serializeScript`, so nothing is silently hidden or reinterpreted.
 */
function parseLeadingText(lead: string): {
  clipHeader: string | null
  globalPreamble: string | null
} {
  const trimmed = lead.trim()
  if (!trimmed) {
    return { clipHeader: null, globalPreamble: null }
  }

  const lines = trimmed.split(/\r?\n/)
  const firstLine = lines[0]?.trim() ?? ''

  // Preserve the existing `Clip 1:` / `Scene [A]:` header support, but only
  // when the label IS the whole lead. A colon-terminated line followed by more
  // text is a clip-wide directive whose value happens to start on the next line
  // (`SEATING LOCK:\n@[[Orest]] sits left.`); treating that as a header would
  // strip the label out of the preamble and render it as a title.
  if (lines.length === 1 && firstLine.endsWith(':') && !TIMESTAMP_RE.test(firstLine)) {
    return { clipHeader: firstLine.replace(/:\s*$/, '').trim(), globalPreamble: null }
  }

  return { clipHeader: null, globalPreamble: trimmed || null }
}

/** Remove the `PREVIOUS SHOT CONTEXT` paragraph from the action region (text
 * before the first tag). It is written after the preamble, but older prompts
 * carry it after the last segment; without this the preamble parser or the
 * segment parser would absorb it. The paragraph runs to the next line that
 * starts with a timestamp, or to the end of the action region. */
function extractPreviousShotContext(text: string): {
  text: string
  previousShotContext: string | null
} {
  const tagIdx = text.search(/<[a-z_]+>/i)
  const actionEnd = tagIdx >= 0 ? tagIdx : text.length
  const actionBlock = text.slice(0, actionEnd)
  const start = actionBlock.search(PREVIOUS_SHOT_CONTEXT_RE)
  if (start < 0) return { text, previousShotContext: null }

  const nextTs = actionBlock.slice(start).search(LINE_START_TIMESTAMP_RE)
  const end = nextTs >= 0 ? start + nextTs : actionEnd
  const previousShotContext = actionBlock.slice(start, end).trim()
  const before = text.slice(0, start).replace(/\s+$/, '')
  const after = text.slice(end)
  const rest = after.replace(/^\s+/, '')
  return {
    text: before && rest ? `${before}\n${rest}` : before || rest,
    previousShotContext,
  }
}

/* ── parseScript ── */

export function parseScript(
  rawText: string,
  knownSpeakerIndex: SpeakerNameIndex = EMPTY_SPEAKER_NAME_INDEX,
): ParsedScript {
  const text = rawText ?? ''
  const trimmed = text.trim()

  // Empty motion prompt → no structured view, but no error either.
  if (!trimmed) {
    return {
      clipHeader: null,
      globalPreamble: null,
      trailingText: null,
      previousShotContext: null,
      videoBlocks: [],
      audioBlocks: [],
      videoOverlap: [],
      audioOverlapCount: 0,
      canStructured: false,
      disableReason: { reason: 'unparseable' },
    }
  }

  // BGM disables structured mode wholesale.
  const bgmMatch = text.match(BGM_LINE_RE)
  if (bgmMatch) {
    return {
      clipHeader: null,
      globalPreamble: null,
      trailingText: null,
      previousShotContext: null,
      videoBlocks: [],
      audioBlocks: [],
      videoOverlap: [],
      audioOverlapCount: 0,
      canStructured: false,
      disableReason: { reason: 'bgm-present', sample: trimSample(bgmMatch[0]) },
    }
  }

  const extracted = extractPreviousShotContext(text)
  const bodyText = extracted.text
  const parsed = parseMotionPrompt(bodyText, knownSpeakerIndex)

  if (parsed.fallbackText || parsed.segments.length === 0) {
    return {
      clipHeader: parsed.clipHeader,
      globalPreamble: null,
      trailingText: null,
      previousShotContext: null,
      videoBlocks: [],
      audioBlocks: [],
      videoOverlap: [],
      audioOverlapCount: 0,
      canStructured: false,
      disableReason: { reason: 'unparseable' },
    }
  }

  // Split the text before the first timestamp off as the clip-wide preamble.
  // Whatever shape it has, it is preserved and rendered — Structured mode is
  // about editing the timestamped body, so unrecognized lead prose is no reason
  // to refuse the whole prompt.
  const tagIdx = bodyText.search(/<(dialogue|voiceover)>/i)
  const actionBlock = tagIdx >= 0 ? bodyText.slice(0, tagIdx) : bodyText
  const firstTsIdx = actionBlock.search(TIMESTAMP_RE)
  const lead =
    firstTsIdx > 0
      ? actionBlock.slice(0, firstTsIdx)
      : firstTsIdx < 0 && tagIdx >= 0
        ? actionBlock
        : ''
  const leading = parseLeadingText(lead)

  // Build blocks.
  const videoBlocks: ScriptBlock[] = []
  const audioBlocks: ScriptBlock[] = []
  for (const seg of parsed.segments) {
    const blk = segmentToBlock(seg)
    if (blk.kind === 'video') videoBlocks.push(blk)
    else audioBlocks.push(blk)
  }
  videoBlocks.sort((a, b) => a.startSec - b.startSec)
  audioBlocks.sort((a, b) => a.startSec - b.startSec)

  const videoOverlap = detectOverlapPairs(videoBlocks)
  const audioMax = maxConcurrent(audioBlocks)
  const audioOverlapCount = audioMax >= 2 ? 2 : audioMax >= 1 ? 1 : 0

  return {
    clipHeader: leading.clipHeader ?? (leading.globalPreamble ? null : parsed.clipHeader),
    globalPreamble: leading.globalPreamble,
    trailingText: parsed.trailingText,
    previousShotContext: extracted.previousShotContext,
    videoBlocks,
    audioBlocks,
    videoOverlap,
    audioOverlapCount,
    canStructured: videoBlocks.length + audioBlocks.length > 0,
    disableReason: null,
  }
}

/* ── serializeScript ── */

function escapeBody(s: string): string {
  return s.replace(/\s+$/g, '')
}

function serializeVideoBlock(b: ScriptBlock): string {
  const ts = `[${formatSec(b.startSec)}-${formatSec(b.endSec)}]`
  let out = `${ts}: ${escapeBody(b.text)}`
  if (b.camera && b.camera.trim()) {
    out += `\n  Camera: ${b.camera.trim()}`
  }
  return out
}

function serializeSpeechBlock(b: ScriptBlock): string {
  const ts = `[${formatSec(b.startSec)}-${formatSec(b.endSec)}]`
  if (b.kind === 'sfx') {
    return `${ts}: SFX: ${escapeBody(b.text)}`
  }
  // dialogue / voiceover
  const character = (b.character ?? '').trim()
  const emotion = (b.emotion ?? '').trim()
  const speakerInner = emotion ? `${character},${emotion}` : character
  const body = escapeBody(b.text)
  // Skip `""` wrapper when body is empty so reopening the block doesn't
  // surface literal quote characters in the editor. `[@Alice]:` (with or
  // without speaker) round-trips cleanly through `parseTimestampedSegments`.
  if (!body) {
    return speakerInner ? `${ts}: [@${speakerInner}]:` : `${ts}:`
  }
  const speaker = speakerInner ? `[@${speakerInner}]: ` : ''
  return `${ts}: ${speaker}"${body}"`
}

export function serializeScript(parsed: {
  clipHeader: string | null
  globalPreamble?: string | null
  trailingText?: string | null
  previousShotContext?: string | null
  videoBlocks: ScriptBlock[]
  audioBlocks: ScriptBlock[]
}): string {
  const parts: string[] = []

  // Action block.
  const video = [...parsed.videoBlocks].sort((a, b) => a.startSec - b.startSec)
  const speech = [...parsed.audioBlocks].sort((a, b) => a.startSec - b.startSec)
  const dialogueBlocks = speech.filter((b) => b.kind === 'dialogue' || b.kind === 'sfx')
  const voiceoverBlocks = speech.filter((b) => b.kind === 'voiceover')

  if (parsed.clipHeader) {
    parts.push(`${parsed.clipHeader}:`)
  }
  if (parsed.globalPreamble) {
    parts.push(parsed.globalPreamble.trim())
    parts.push('')
  }
  // Clip-wide, so it sits with the preamble ahead of the timeline — where the
  // motion-prompt-writing skill places it. Older prompts carry it after the last
  // segment; the parser accepts either and this writes it back at the top.
  if (parsed.previousShotContext) {
    parts.push(parsed.previousShotContext.trim())
    parts.push('')
  }
  for (const b of video) {
    parts.push(serializeVideoBlock(b))
  }

  let out = parts.join('\n')

  if (dialogueBlocks.length > 0) {
    if (out.length > 0) out += '\n'
    out += '<dialogue>\n'
    out += dialogueBlocks.map(serializeSpeechBlock).join('\n')
  }
  if (voiceoverBlocks.length > 0) {
    if (out.length > 0) out += '\n'
    out += '<voiceover>\n'
    out += voiceoverBlocks.map(serializeSpeechBlock).join('\n')
  }

  // Trailing content (e.g. a `<voices>` roster) is re-emitted after the blocks,
  // separated by a blank line the same way the parser found it. Without this it
  // would round-trip inside the last dialogue block's quoted body.
  if (parsed.trailingText) {
    if (out.length > 0) out += '\n\n'
    out += parsed.trailingText.trim()
  }

  return out
}
